import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FC, FormEvent, PropsWithChildren, useState } from 'react'

import { APIClient } from '../APIClient'
import { Utils } from '../Utils'

import Setup2FAPage from '../pages/Setup2FAPage'
import SharedChatPage from '../pages/SharedChatPage'
import useBrand from '../tenant/useBrand'

import { backendLogin, backendVerify2FA } from './backendAuth'
import { isAuthenticated } from './session'

type LoginStep = 'LOGIN' | 'VERIFY_2FA'

/**
 * Replaces the Amplify `AuthLayout` gate. If there is no stored session token,
 * shows the split-screen login (form on the left, brand hero on the right)
 * that authenticates against aristotle-backend; otherwise renders the app.
 */
const LoginScreen: FC = () => {
  const brand = useBrand()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // 2FA flow state.
  const [step, setStep] = useState<LoginStep>('LOGIN')
  const [tempToken, setTempToken] = useState('')
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)

  // After a successful login (with or without 2FA), either go straight to the
  // app or, for a first-time user who has never seen the 2FA prompt, send them
  // to the standalone authenticator setup page first.
  const proceedAfterAuth = (hasSeenTwoFactorSetup?: boolean) => {
    window.location.assign(hasSeenTwoFactorSetup === false ? '/setup-2fa' : '/app')
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setSubmitting(true)
    try {
      const result = await backendLogin(email.trim(), password)
      if (result.status === 'requires2FA') {
        setTempToken(result.tempToken)
        setStep('VERIFY_2FA')
        setSubmitting(false)
        return
      }
      proceedAfterAuth(result.hasSeenTwoFactorSetup)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Check your credentials.')
      setSubmitting(false)
    }
  }

  const onVerify2FA = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.')
      return
    }
    setSubmitting(true)
    try {
      const result = await backendVerify2FA(tempToken, code, trustDevice)
      proceedAfterAuth(result.hasSeenTwoFactorSetup)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.')
      setSubmitting(false)
    }
  }

  const onForgotPassword = async () => {
    setError(null)
    if (!email.trim()) {
      setNotice('Enter your email above, then click "Forgot password?" again.')
      return
    }
    try {
      await APIClient.makeAPICall('POST', '/auth/forget-password', {
        body: { email: email.trim() },
      })
    } catch {
      /* don't reveal whether the email exists */
    }
    setNotice('If that email exists, we’ve sent password reset instructions.')
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left: sign-in form */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 45%' },
          bgcolor: '#ededed',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            {brand.logoUrl && (
              <Box
                alt={brand.loginName}
                component="img"
                src={brand.logoUrl}
                sx={{ height: 48, maxWidth: 200, objectFit: 'contain' }}
              />
            )}
            <Typography
              align="center"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                fontSize: '1.75rem',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {brand.loginName}
            </Typography>
          </Stack>

          <Box
            component="form"
            onSubmit={step === 'VERIFY_2FA' ? onVerify2FA : onSubmit}
            sx={{
              bgcolor: 'common.white',
              borderRadius: 3,
              p: 4,
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            }}
          >
            <Stack spacing={2.5}>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
                {step === 'LOGIN' && 'Sign In'}
                {step === 'VERIFY_2FA' && 'Two-Factor Authentication'}
              </Typography>

              {step === 'VERIFY_2FA' && (
                <Typography sx={{ color: 'grey.600' }} variant="body2">
                  Enter the 6-digit code from your authenticator app.
                </Typography>
              )}

              {error && (
                <Alert severity="error" variant="standard">
                  {error}
                </Alert>
              )}
              {notice && (
                <Alert severity="info" variant="standard">
                  {notice}
                </Alert>
              )}

              {step === 'LOGIN' && (
                <>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Email"
                    size="medium"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />

                  <Box>
                    <TextField
                      fullWidth
                      label="Password"
                      size="medium"
                      type="password"
                      value={password}
                      variant="filled"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Box sx={{ textAlign: 'right', mt: 1 }}>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                        onClick={onForgotPassword}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  </Box>

                  <Button
                    color="primary"
                    disabled={submitting || !email || !password}
                    size="large"
                    type="submit"
                    variant="contained"
                    sx={{ borderRadius: 9999, py: 1.2 }}
                  >
                    {submitting ? 'Signing in…' : 'Sign In'}
                  </Button>
                </>
              )}

              {step === 'VERIFY_2FA' && (
                <>
                  <TextField
                    autoFocus
                    fullWidth
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      style: { textAlign: 'center', letterSpacing: '0.4em' },
                    }}
                    label="Authenticator Code"
                    placeholder="000000"
                    size="medium"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={trustDevice}
                        onChange={(e) => setTrustDevice(e.target.checked)}
                      />
                    }
                    label="Trust this device for 30 days"
                  />

                  <Button
                    color="primary"
                    disabled={submitting || code.length !== 6}
                    size="large"
                    type="submit"
                    variant="contained"
                    sx={{ borderRadius: 9999, py: 1.2 }}
                  >
                    {submitting ? 'Verifying…' : 'Verify Code'}
                  </Button>

                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    sx={{ textAlign: 'center' }}
                    onClick={() => {
                      setStep('LOGIN')
                      setError(null)
                      setCode('')
                    }}
                  >
                    Back to sign in
                  </Link>
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Right: black brand hero (org logo when available, else default) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          flex: '1 1 55%',
          bgcolor: '#000',
          backgroundImage: `url(${brand.logoUrl ?? Utils.requireFromCDN(brand.loginImage)})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
    </Box>
  )
}

const AuthGate: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [authed] = useState(isAuthenticated())

  // Shared-chat viewer routes live outside the app shell. Public links open for
  // anyone; private links still require a signed-in session (the backend then
  // checks the link's email against the user).
  const path = window.location.pathname
  if (path.startsWith('/chat/private/share/')) {
    if (!authed) return <LoginScreen />
    return <SharedChatPage isPrivate />
  }
  if (path.startsWith('/chat/share/')) {
    return <SharedChatPage />
  }

  if (!authed) return <LoginScreen />

  // First-time 2FA onboarding lives outside the app shell (focused full-screen
  // page), reached from the login flow when the user has never seen the prompt.
  if (path === '/setup-2fa') return <Setup2FAPage />

  return <>{children}</>
}

export default AuthGate
